/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { Col, Row, Table, Card, Container, Badge } from 'react-bootstrap'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import api from '../../config/AxiosInstance'
import Loading from '../../components/atoms/Loading'
import toast, { Toaster } from 'react-hot-toast'
import { convertDateWithTime } from '../../helpers/constants'
import {
  Warehouse,
  Calendar,
  User,
  StickyNote,
  ArrowLeft,
  FileSpreadsheet,
  Tag,
  UserCog,
  Clock,
  Package
} from 'lucide-react'

const labelDetailStyle = {
  fontSize: '0.75rem',
  fontWeight: '600',
  color: '#888',
  textTransform: 'uppercase',
  marginBottom: '4px',
  letterSpacing: '0.5px'
}

const valueDetailStyle = {
  fontSize: '0.95rem',
  fontWeight: '500',
  color: '#333333'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

function DetailStockAdjustment() {
  const { id } = useParams()
  const { state } = useLocation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)

  const renderAdjustmentType = (data) => {
    if (data.adjustment_type === 'addition') {
      return (
        <Badge bg="success" className="px-3 py-2 rounded-pill">
          Addition (+)
        </Badge>
      )
    }
    if (data.adjustment_type === 'reduction') {
      return (
        <Badge bg="danger" className="px-3 py-2 rounded-pill">
          Reduction (-)
        </Badge>
      )
    }
    return '-'
  }

  const columns = [
    { key: 'row_number', label: 'No' },
    {
      key: 'item_code_name',
      label: 'Item Code - Name',
      component: (item) => `${item.item_code} - ${item.item_name}`
    },
    { key: 'batch', label: 'Batch', className: 'text-end align-end' },
    {
      key: 'slot',
      label: 'Slot',
      component: (data) => data?.slot ?? '-',
      className: 'text-end align-end'
    },
    {
      key: 'pallet',
      label: 'Pallet',
      component: (data) => data?.pallet ?? '-',
      className: 'text-end align-end'
    },
    {
      key: 'adjustment_type',
      label: 'Adjustment Type',
      component: (data) => renderAdjustmentType(data),
      className: 'text-end align-end'
    },
    {
      key: 'begin_pack',
      label: 'Begin Pack',
      component: (data) => data?.begin_pack ?? '-',
      className: 'text-end align-end'
    },
    {
      key: 'adjustment_pack',
      label: 'Adjustment Pack',
      className: 'text-end align-end'
    },
    {
      key: 'ending_pack',
      label: 'Ending Pack',
      component: (data) => data?.ending_pack ?? '-',
      className: 'text-end align-end'
    }
  ]

  const getDetail = async () => {
    try {
      setLoading(true)
      const response = await api.get(`stock-adjustment/detail/${id}`)
      const { data } = response.data
      setData(data)
    } catch (err) {
      const errMsg =
        err?.response?.data?.errors?.message ||
        err?.message ||
        'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      navigate(-1)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getDetail()
  }, [id])

  if (loading) {
    return <Loading />
  }

  return (
    <Container fluid className="px-4 mb-5">
      <div className="d-flex align-items-center mb-4">
        <button
          className="btn btn-link text-decoration-none text-dark p-0 me-3"
          onClick={() => navigate('/stock-adjustment', { state })}
        >
          <ArrowLeft size={24} />
        </button>
        <div>
          <h4 className="mb-0 fw-bold text-dark">
            {data?.code || state?.code || 'Detail Stock Adjustment'}
          </h4>
          <div className="text-muted small">
            Stock Adjustment Detail & Items
          </div>
        </div>
      </div>

      <Row className="gy-4">
        <Col md={12}>
          <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
            <Card.Body className="p-4">
              <Row className="g-4">
                <Col md={3}>
                  <div className="d-flex align-items-start">
                    <div className="p-3 bg-light rounded me-3 text-dark">
                      <Tag size={24} />
                    </div>
                    <div>
                      <div style={labelDetailStyle}>Code</div>
                      <div style={valueDetailStyle}>{data?.code || '-'}</div>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-start">
                    <div className="p-3 bg-light rounded me-3 text-primary">
                      <Warehouse size={24} />
                    </div>
                    <div>
                      <div style={labelDetailStyle}>Warehouse</div>
                      <div style={valueDetailStyle}>
                        {data?.warehouse_id || '-'}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-start">
                    <div className="p-3 bg-light rounded me-3 text-success">
                      <Package size={24} />
                    </div>
                    <div>
                      <div style={labelDetailStyle}>Type</div>
                      <div style={valueDetailStyle}>
                        {data?.is_vas ? (
                          <Badge
                            bg="warning"
                            text="dark"
                            className="px-2 py-1 text-uppercase"
                          >
                            VAS
                          </Badge>
                        ) : data?.is_packing ? (
                          <Badge bg="info" className="px-2 py-1 text-uppercase">
                            Packing
                          </Badge>
                        ) : (
                          <Badge
                            bg="secondary"
                            className="px-2 py-1 text-uppercase"
                          >
                            Item
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-start">
                    <div className="p-3 bg-light rounded me-3 text-info">
                      <User size={24} />
                    </div>
                    <div>
                      <div style={labelDetailStyle}>Created By</div>
                      <div style={valueDetailStyle}>
                        {data?.created_by || '-'}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-start">
                    <div className="p-3 bg-light rounded me-3 text-warning">
                      <Calendar size={24} />
                    </div>
                    <div>
                      <div style={labelDetailStyle}>Created At</div>
                      <div style={valueDetailStyle}>
                        {data?.created_at
                          ? convertDateWithTime(data?.created_at)
                          : '-'}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-start">
                    <div className="p-3 bg-light rounded me-3 text-primary">
                      <UserCog size={24} />
                    </div>
                    <div>
                      <div style={labelDetailStyle}>Updated By</div>
                      <div style={valueDetailStyle}>
                        {data?.updated_by || '-'}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-start">
                    <div className="p-3 bg-light rounded me-3 text-danger">
                      <Clock size={24} />
                    </div>
                    <div>
                      <div style={labelDetailStyle}>Updated At</div>
                      <div style={valueDetailStyle}>
                        {data?.updated_at
                          ? convertDateWithTime(data?.updated_at)
                          : '-'}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-start">
                    <div className="p-3 bg-light rounded me-3 text-secondary">
                      <StickyNote size={24} />
                    </div>
                    <div>
                      <div style={labelDetailStyle}>Notes</div>
                      <div style={valueDetailStyle}>{data?.note || '-'}</div>
                    </div>
                  </div>
                </Col>
                {data?.file_url && (
                  <Col md={3}>
                    <div className="d-flex align-items-start">
                      <div className="p-3 bg-light rounded me-3 text-success">
                        <FileSpreadsheet size={24} />
                      </div>
                      <div>
                        <div style={labelDetailStyle}>Reference File</div>
                        <div style={valueDetailStyle}>
                          <a
                            href={data.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-underline"
                          >
                            Download File
                          </a>
                        </div>
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col md={12}>
          <Card className="border-0 shadow-sm rounded-4">
            <Card.Body className="p-0">
              <Table
                hover
                responsive
                className="mb-0"
                style={{ fontSize: '0.9rem' }}
              >
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    {columns.map((column) => (
                      <th
                        className={`${column.className ?? 'align-middle'} p-2`}
                        key={column.key}
                        style={tableHeaderStyle}
                      >
                        <span>{column.label}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data?.items.length === 0 && (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="p-5 text-center text-muted fw-bold"
                      >
                        No data available
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    data?.items?.length > 0 &&
                    data?.items?.map((item, indexItem) => (
                      <tr
                        key={indexItem}
                        style={{ borderBottom: '1px solid #f0f0f0' }}
                      >
                        {columns.map((column) => (
                          <td
                            key={column.key}
                            className={`${
                              column.className ?? 'align-middle'
                            } p-3`}
                          >
                            {column.key === 'row_number' && (
                              <span className="fw-semibold text-muted">
                                {indexItem + 1}
                              </span>
                            )}
                            {column.component && column.component(item)}
                            {!column.component &&
                              column.key !== 'row_number' && (
                                <span
                                  className={
                                    column.key === 'item_name'
                                      ? 'fw-bold text-dark'
                                      : 'text-secondary'
                                  }
                                >
                                  {item[column.key]}
                                </span>
                              )}
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Toaster />
    </Container>
  )
}

export default DetailStockAdjustment
